/**
 * The three states every list on this panel has to render besides its data:
 * still loading, failed, and genuinely empty.
 *
 * They are separate small components rather than one wrapper because the empty
 * copy differs everywhere — an empty store list means "nobody has set one up",
 * an empty fleet means "no TV has been paired" — and a generic "No results"
 * would be worse than useless to the person who has to act on it.
 */

import type { ReactElement, ReactNode } from 'react';
import { errorMessage, isApiError } from '../lib/api.js';

export function LoadingBlock({ label }: { label: string }): ReactElement {
  return (
    <div className="state-block" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <p className="state-block__text">{label}</p>
    </div>
  );
}

export interface ErrorBlockProps {
  error: unknown;
  /** What was being attempted, e.g. "load the stores". */
  action: string;
  onRetry?: () => void;
}

export function ErrorBlock({ error, action, onRetry }: ErrorBlockProps): ReactElement {
  // A route that exists but has not shipped yet answers 501 and names the
  // milestone it arrives in. That is a schedule, not a fault, and reads better
  // as information than as a red box telling someone to call for help.
  const notShippedYet = isApiError(error) && error.status === 501;

  return (
    <div
      className={notShippedYet ? 'state-block state-block--info' : 'state-block state-block--error'}
      role={notShippedYet ? 'status' : 'alert'}
    >
      <p className="state-block__title">
        {notShippedYet ? 'Not available yet' : `Could not ${action}`}
      </p>
      <p className="state-block__text">{errorMessage(error)}</p>
      {onRetry !== undefined && !notShippedYet ? (
        <button type="button" className="button button--ghost" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

export interface InlineEmptyProps {
  title: string;
  description: string;
  children?: ReactNode;
}

export function InlineEmpty({ title, description, children }: InlineEmptyProps): ReactElement {
  return (
    <div className="state-block">
      <p className="state-block__title">{title}</p>
      <p className="state-block__text">{description}</p>
      {children}
    </div>
  );
}

/** A short, non-alarming note: scope explanations, "read-only for your role", and such. */
export function InfoNote({ children }: { children: ReactNode }): ReactElement {
  return (
    <p className="alert alert--info" role="note">
      {children}
    </p>
  );
}
